/**
 * R268ff · Feb 2026 — Record Payment dialog for GST Sales docs.
 *
 * Supports multi-currency payments. When the doc is in a foreign
 * currency, captures what actually hit the bank in the home
 * currency, minus wire/FX charges (R268w).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtMoney } from "@/lib/currency";

export default function PaymentDialog({ doc, ws, profile, onClose, onDone }) {
  const [amount, setAmount] = useState(0);
  const [mode, setMode] = useState("upi");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const docCurrency = doc?.currency || doc?.totals?.currency || "INR";
  const home = profile?.home_currency || "INR";
  const isForeign = docCurrency !== home;
  const [remittedAmount, setRemittedAmount] = useState(0);
  const [bankCharges, setBankCharges] = useState(0);
  const [fxRate, setFxRate] = useState(1);

  useEffect(() => {
    if (doc) {
      const t = doc.totals || {};
      const paid = doc.paid_total || 0;
      const tds = t.tds_amount || 0;
      const outstanding = Math.max((t.grand_total || 0) - paid - tds, 0);
      setAmount(outstanding);
      setMode(isForeign ? "swift" : "upi"); setReference("");
      const seedFx = doc.fx_rate || t.fx_rate || 1;
      setFxRate(seedFx);
      setRemittedAmount(Number((outstanding * seedFx).toFixed(2)));
      setBankCharges(0);
    }
  }, [doc, isForeign]);

  if (!doc) return null;

  const submit = async () => {
    if (!(amount > 0)) { toast.error("Amount must be positive"); return; }
    setSaving(true);
    try {
      const payload = { amount: Number(amount), mode, reference: reference || null };
      if (isForeign) {
        payload.remitted_amount = Number(remittedAmount) || 0;
        payload.remitted_currency = home;
        payload.bank_charges = Number(bankCharges) || 0;
        payload.fx_rate = Number(fxRate) || 0;
      }
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/payments`, payload);
      toast.success("Payment recorded");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="gst-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment · {doc.doc_no}</DialogTitle>
          <DialogDescription>
            Grand total {fmtMoney(doc.totals?.grand_total, docCurrency)} · Already paid {fmtMoney(doc.paid_total || 0, docCurrency)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Amount ({docCurrency})</Label>
            <Input type="number" inputMode="decimal" className="h-11" value={amount}
                      data-testid="gst-payment-amount"
                      onChange={(e) => {
                        setAmount(e.target.value);
                        if (isForeign) {
                          setRemittedAmount(Number((Number(e.target.value || 0) * Number(fxRate || 0)).toFixed(2)));
                        }
                      }} />
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-11" data-testid="gst-payment-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["upi", "neft", "rtgs", "imps", "cheque", "cash", "card",
                  "swift", "wire", "paypal", "wise", "stripe", "other"].map((m) => (
                  <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reference / UTR</Label>
            <Input className="h-11" value={reference}
                      data-testid="gst-payment-reference"
                      onChange={(e) => setReference(e.target.value)} />
          </div>
          {isForeign && (
            <div className="border border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40 p-3 rounded space-y-2"
                    data-testid="gst-payment-fx-section">
              <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                Final remittance in {home}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">FX rate ({docCurrency}→{home})</Label>
                  <Input type="number" inputMode="decimal" className="h-11"
                            data-testid="gst-payment-fx-rate"
                            value={fxRate}
                            onChange={(e) => {
                              setFxRate(e.target.value);
                              setRemittedAmount(Number((Number(amount || 0) * Number(e.target.value || 0)).toFixed(2)));
                            }} />
                </div>
                <div>
                  <Label className="text-[11px]">Bank / wire charges ({home})</Label>
                  <Input type="number" inputMode="decimal" className="h-11"
                            data-testid="gst-payment-bank-charges"
                            value={bankCharges}
                            onChange={(e) => setBankCharges(e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Amount actually received ({home}, gross)</Label>
                <Input type="number" inputMode="decimal" className="h-11"
                          data-testid="gst-payment-remitted"
                          value={remittedAmount}
                          onChange={(e) => setRemittedAmount(e.target.value)} />
              </div>
              <div className="text-[11px] text-emerald-800 dark:text-emerald-200">
                Net after charges: <b>{fmtMoney(
                  Math.max(Number(remittedAmount || 0) - Number(bankCharges || 0), 0),
                  home,
                )}</b>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-11">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-11"
                    data-testid="gst-payment-save">
            {saving ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
